import React, { useCallback, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '../ui/Card';
import { Input } from '../ui/Input';
import { MailIcon, CalendarIcon, HardDriveIcon, FileTextIcon, TableIcon, PresentationIcon, ClipboardListIcon, CheckCircle2Icon, ShieldCheckIcon, Loader2Icon, SparklesIcon, ArrowRightIcon, XIcon } from 'lucide-react';
// ─── Google Logo SVG ────────────────────────────────────────────────────────
function GoogleLogo({
  size = 32
}: {
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" data-id="element-2350">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" data-id="element-2351" />

      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-2352" />

      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" data-id="element-2353" />

      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-2354" />
    </svg>;
}
// ─── Service Icons ──────────────────────────────────────────────────────────
const SERVICES = [{
  id: 'gmail',
  name: 'Gmail',
  icon: MailIcon,
  color: 'text-red-400',
  bg: 'bg-red-500/10'
}, {
  id: 'calendar',
  name: 'Calendar',
  icon: CalendarIcon,
  color: 'text-blue-400',
  bg: 'bg-blue-500/10'
}, {
  id: 'drive',
  name: 'Drive',
  icon: HardDriveIcon,
  color: 'text-emerald-400',
  bg: 'bg-emerald-500/10'
}, {
  id: 'docs',
  name: 'Docs',
  icon: FileTextIcon,
  color: 'text-blue-400',
  bg: 'bg-blue-500/10'
}, {
  id: 'sheets',
  name: 'Sheets',
  icon: TableIcon,
  color: 'text-emerald-400',
  bg: 'bg-emerald-500/10'
}, {
  id: 'slides',
  name: 'Slides',
  icon: PresentationIcon,
  color: 'text-amber-400',
  bg: 'bg-amber-500/10'
}, {
  id: 'forms',
  name: 'Forms',
  icon: ClipboardListIcon,
  color: 'text-purple-400',
  bg: 'bg-purple-500/10'
}] as const;
type ServiceId = (typeof SERVICES)[number]['id'];
// ─── Accent Color Mapping ───────────────────────────────────────────────────
const ACCENT_COLORS = {
  red: {
    glow: 'bg-red-500/10',
    border: 'border-red-500/30',
    ring: 'ring-red-500/20',
    button: 'from-red-500 to-red-600',
    text: 'text-red-400'
  },
  blue: {
    glow: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    ring: 'ring-blue-500/20',
    button: 'from-blue-500 to-blue-600',
    text: 'text-blue-400'
  },
  emerald: {
    glow: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    ring: 'ring-emerald-500/20',
    button: 'from-emerald-500 to-emerald-600',
    text: 'text-emerald-400'
  },
  amber: {
    glow: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    ring: 'ring-amber-500/20',
    button: 'from-amber-500 to-amber-600',
    text: 'text-amber-400'
  },
  purple: {
    glow: 'bg-purple-500/10',
    border: 'border-purple-500/30',
    ring: 'ring-purple-500/20',
    button: 'from-purple-500 to-purple-600',
    text: 'text-purple-400'
  }
} as const;
type AccentColor = keyof typeof ACCENT_COLORS;
// ─── Props ──────────────────────────────────────────────────────────────────
interface GoogleConnectCardProps {
  /** Title shown in the card (e.g., 'Gmail', 'Google Calendar') */
  title?: string;
  /** Custom description text */
  description?: string;
  /** Accent color for theming */
  accentColor?: AccentColor;
  /** Which service icon to highlight */
  highlightService?: ServiceId;
  /** Callback when connection succeeds */
  onConnected?: () => void;
  /** Sign-in function that accepts email */
  signIn: (email: string) => Promise<void>;
  /** Error from auth hook */
  error?: string | null;
  /** Whether auth is loading */
  isLoading?: boolean;
  /** Layout variant: 'inline' fits inside panels, 'fullscreen' is standalone page */
  variant?: 'fullscreen' | 'inline';
}
// ─── Component ──────────────────────────────────────────────────────────────
export function GoogleConnectCard({
  title = 'Google Workspace',
  description,
  accentColor = 'blue',
  highlightService,
  onConnected,
  signIn,
  error: externalError,
  isLoading: externalLoading,
  variant = 'inline'
}: GoogleConnectCardProps) {
  const [email, setEmail] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [step, setStep] = useState<'email' | 'connecting' | 'success'>('email');
  const accent = ACCENT_COLORS[accentColor];
  const error = localError || externalError;
  const isLoading = isConnecting || externalLoading;
  const isInline = variant === 'inline';
  // Validate email format
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const handleConnect = useCallback(async () => {
    if (!isValidEmail) {
      setLocalError('Ingresá un email válido de Google');
      return;
    }
    setLocalError(null);
    setIsConnecting(true);
    setStep('connecting');
    try {
      await signIn(email);
      setStep('success');
      setTimeout(() => {
        onConnected?.();
      }, 1500);
    } catch (err: any) {
      setStep('email');
      setLocalError(err.message || 'Error al conectar con Google');
    } finally {
      setIsConnecting(false);
    }
  }, [email, isValidEmail, signIn, onConnected]);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isValidEmail && !isLoading) {
      handleConnect();
    }
  };
  // Find highlighted service
  const highlightedService = highlightService ? SERVICES.find(s => s.id === highlightService) : null;
  return <div className={isInline ? 'h-full overflow-y-auto flex items-center justify-center p-4 lg:p-8' : 'h-full overflow-y-auto p-4 lg:p-8 bg-[#020617] relative flex items-center justify-center'} data-id="element-2355">
      {/* Background glow — only in fullscreen */}
      {!isInline && <div className={`absolute top-0 left-1/4 w-1/2 h-64 ${accent.glow} blur-[120px] rounded-full pointer-events-none`} data-id="element-2356" />}

      <Card className={`max-w-lg w-full bg-slate-900/60 backdrop-blur-xl border-slate-800 relative ${isInline ? '' : 'z-10'} shadow-2xl overflow-hidden`} data-id="element-2357">
        {/* Animated gradient border */}
        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full animate-[shimmer_3s_infinite]" data-id="element-2358" />

        <div className="relative p-8" data-id="element-2359">
          <AnimatePresence mode="wait" data-id="element-2360">
            {step === 'success' ?
          // ─── Success State ─────────────────────────────────────────
          <motion.div key="success" initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} className="text-center py-8" data-id="element-2361">
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              delay: 0.1
            }} className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-emerald-500/30" data-id="element-2362">
                  <CheckCircle2Icon size={40} className="text-emerald-400" data-id="element-2363" />
                </motion.div>
                <h2 className="text-2xl font-bold text-slate-100 mb-2" data-id="element-2364">
                  ¡Conectado!
                </h2>
                <p className="text-slate-400 text-sm" data-id="element-2365">
                  Tu cuenta de Google está lista para usar
                </p>
              </motion.div> :
          // ─── Email Input State ─────────────────────────────────────
          <motion.div key="email" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} data-id="element-2366">
                {/* Header */}
                <div className="text-center mb-8" data-id="element-2367">
                  <div className="flex items-center justify-center gap-3 mb-4" data-id="element-2368">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center border ${accent.border} ${accent.glow} shadow-lg`} data-id="element-2369">
                      {highlightedService ? <highlightedService.icon size={32} className={highlightedService.color} data-id="element-2370" /> : <GoogleLogo size={32} data-id="element-2371" />}
                    </div>
                  </div>

                  <h2 className="text-2xl font-bold text-slate-100 mb-2" data-id="element-2372">
                    Conectar {title}
                  </h2>
                  <p className="text-slate-400 text-sm leading-relaxed max-w-sm mx-auto" data-id="element-2373">
                    {description || `Conectá tu cuenta de Google para acceder a ${title} directamente desde el dashboard.`}
                  </p>
                </div>

                {/* Services Grid */}
                <div className="mb-6" data-id="element-2374">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 text-center" data-id="element-2375">
                    Servicios incluidos
                  </p>
                  <div className="flex flex-wrap justify-center gap-2" data-id="element-2376">
                    {SERVICES.map(service => {
                  const isHighlighted = service.id === highlightService;
                  return <div key={service.id} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border transition-all ${isHighlighted ? `${service.bg} border-current ${service.color}` : 'bg-slate-800/50 border-slate-700/50 text-slate-400'}`} data-id="element-2377">
                          <service.icon size={14} data-id="element-2378" />
                          <span className="text-xs font-medium" data-id="element-2379">
                            {service.name}
                          </span>
                        </div>;
                })}
                  </div>
                </div>

                {/* Email Input */}
                <div className="space-y-4" data-id="element-2380">
                  <div className="relative" data-id="element-2381">
                    <Input type="email" placeholder="tu@gmail.com" value={email} onChange={e => {
                  setEmail(e.target.value);
                  setLocalError(null);
                }} onKeyDown={handleKeyDown} disabled={isLoading} error={error || undefined} className="pr-10 text-center text-base" data-id="element-2382" />

                    {email && <button type="button" onClick={() => setEmail('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors" data-id="element-2383">
                        <XIcon size={16} data-id="element-2384" />
                      </button>}
                  </div>

                  {/* Connect Button */}
                  <button onClick={handleConnect} disabled={!isValidEmail || isLoading} className={`w-full py-3.5 px-4 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-3 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${isLoading ? 'bg-slate-800 text-slate-400' : `bg-gradient-to-r ${accent.button} text-white hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]`}`} data-id="element-2385">
                    {isLoading ? <>
                        <Loader2Icon size={18} className="animate-spin" data-id="element-2386" />
                        <span data-id="element-2387">Conectando...</span>
                      </> : <>
                        <GoogleLogo size={20} data-id="element-2388" />
                        <span data-id="element-2389">Conectar con Google</span>
                        <ArrowRightIcon size={16} data-id="element-2390" />
                      </>}
                  </button>
                </div>

                {/* Security Note */}
                <div className="mt-6 flex items-start gap-2.5 p-3 bg-slate-800/30 rounded-lg border border-slate-700/50" data-id="element-2391">
                  <ShieldCheckIcon size={16} className="text-emerald-400 shrink-0 mt-0.5" data-id="element-2392" />

                  <p className="text-xs text-slate-400 leading-relaxed" data-id="element-2393">
                    Tus credenciales se almacenan de forma segura en nuestros
                    servidores. Nunca guardamos tu contraseña.
                  </p>
                </div>

                {/* Features hint */}
                <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-500" data-id="element-2394">
                  <SparklesIcon size={12} data-id="element-2395" />
                  <span data-id="element-2396">
                    Una sola conexión para todos los servicios de Google
                  </span>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </Card>
    </div>;
}